'use client'

import FloatScrollToBottomButton from '@/components/molecules/float-scroll-to-bottom-button'
import MessageItem from '@/components/molecules/message-item'
import LiveSupportReviewCard from '@/components/organisms/live-support-review-card'
import { Button } from '@/components/ui/button'
import { ContextMenu, ContextMenuContent, ContextMenuItem, ContextMenuTrigger } from '@/components/ui/context-menu'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Textarea } from '@/components/ui/textarea'
import { useChatContext } from '@/context/ChatProvider'
import { useConfig } from '@/context/ConfigProvider'
import { useSocketContext } from '@/context/SocketProvider'
import useVisibleUserName from '@/hooks/useVisibleUserName'
import { getArrivalArticleGroup } from '@/lib/article-message-order'
import { CHAT_ATTACHMENT_ACCEPT, isChatAttachment } from '@/lib/chat-attachments'
import { isInternalFileUploadedMarker } from '@/lib/internal-message-markers'
import { ChatMessage, useChatStore } from '@/store/chat.store'
import { usePersistStore } from '@/store/persist.store'
import { useSocketStore } from '@/store/socket.store'
import throttle from 'lodash/throttle'
import {
  CreditCard,
  Ellipsis,
  FileText,
  Gift,
  Loader2,
  Paperclip,
  RefreshCcw,
  Send,
  Settings,
  ShieldUser,
  Smile,
  WifiOff,
  XCircle
} from 'lucide-react'
import { useLocale, useTranslations } from 'next-intl'
import { Fragment, useCallback, useEffect, useRef, useState } from 'react'
import ChatModalHeader from '../chat-modal-header'
import TicketsPanel from '../tickets-panel'

export default function ChatInterface() {
  const t = useTranslations('chat')
  const locale = useLocale()
  const tForm = useTranslations('dynamicForm')
  const [emojiOpen, setEmojiOpen] = useState(false)
  const tSubjects = useTranslations('subjects')
  const buildVersion = process.env.NEXT_PUBLIC_BUILD_VERSION || 'dev build'
  const siteId = usePersistStore(state => state.siteId)
  const token = usePersistStore(state => state.token)
  const guestId = usePersistStore(state => state.guestId)
  const isGuestIdActive = usePersistStore(state => state.isGuestIdActive)
  const { apiUrl } = useConfig()
  const sessionId = usePersistStore(state => state.sessionId)
  const setSessionId = usePersistStore(state => state.setSessionId)
  const devMode = usePersistStore(state => state.devMode)
  const setDevMode = usePersistStore(state => state.setDevMode)
  const resetStore = usePersistStore(state => state.resetStore)
  const setIsTakenOver = usePersistStore(state => state.setIsTakenOver)

  const visibleUserName = useVisibleUserName()

  const {
    wsRef,
    sendFrame,
    canSend,
    connectWebSocket,
    endCurrentSession,
    isClosingChat,
    requestMessageHistory,
    handleSelectSubject
  } = useSocketContext()

  const socketConnectionStatus = useSocketStore(state => state.socketConnectionStatus)
  const isMessageResponseWaiting = useSocketStore(state => state.isMessageResponseWaiting)
  const setIsMessageResponseWaiting = useSocketStore(state => state.setIsMessageResponseWaiting)
  const isReconnecting = useSocketStore(state => state.isReconnecting)
  const isAuthSuccess = useSocketStore(state => state.isAuthSuccess)
  const retryAttempt = useSocketStore(state => state.retryAttempt)
  const isPreparing = useSocketStore(state => state.isPreparing)
  const setPendingUploadFileAck = useSocketStore(state => state.setPendingUploadFileAck)

  // Get values from chat store
  const subjects = useChatStore(state => state.subjects)
  const selectedSection = useChatStore(state => state.selectedSection)
  const isShowSectionSelectButtons = useChatStore(state => state.isShowSectionSelectButtons)
  const messages = useChatStore(state => state.messages)
  const setMessages = useChatStore(state => state.setMessages)
  const currentPage = useChatStore(state => state.currentPage)
  const totalPages = useChatStore(state => state.totalPages)
  const isLoadingHistory = useChatStore(state => state.isLoadingHistory)
  const hasMoreHistory = useChatStore(state => state.hasMoreHistory)
  const setShowScrollToBottom = useChatStore(state => state.setShowScrollToBottom)
  const pendingReview = useChatStore(state => state.pendingReview)
  const isGuestInfoRequired = useChatStore(state => state.isGuestInfoRequired)
  const isChatEnded = useChatStore(state => state.isChatEnded)
  const setIsChatEnded = useChatStore(state => state.setIsChatEnded)
  const setSelectedSection = useChatStore(state => state.setSelectedSection)
  const setIsShowSectionSelectButtons = useChatStore(state => state.setIsShowSectionSelectButtons)
  const setIsSecondChatShown = useChatStore(state => state.setIsSecondChatShown)
  const setOperatorName = useChatStore(state => state.setOperatorName)
  const setLiveSupportState = useChatStore(state => state.setLiveSupportState)
  const activeTab = useChatStore(state => state.activeTab)

  // Get scroll functionality from context
  const { scrollToBottom, scrollAreaRef, lastMessageRef } = useChatContext()

  const [input, setInput] = useState('')
  const textareaRef = useRef<HTMLTextAreaElement | null>(null)

  const [selectedFiles, setSelectedFiles] = useState<File[]>([])
  const [filePreviewUrls, setFilePreviewUrls] = useState<string[]>([])
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const previousScrollHeight = useRef<number>(0)
  const newTopicInFlightRef = useRef(false)

  const requiresTopic = !selectedSection && subjects.length > 0 && (isShowSectionSelectButtons || messages.length === 0)

  const attachmentsDisabled =
    !apiUrl ||
    requiresTopic ||
    isMessageResponseWaiting ||
    isGuestInfoRequired ||
    socketConnectionStatus !== 'opened' ||
    isReconnecting ||
    isChatEnded

  const sendMessageDisabled =
    requiresTopic ||
    isMessageResponseWaiting ||
    isGuestInfoRequired ||
    socketConnectionStatus !== 'opened' ||
    isReconnecting ||
    isChatEnded ||
    (!input.trim() && !selectedFiles.length)
  const shouldShowWelcomeMessage = currentPage === totalPages && (token !== 'guest' || !isGuestInfoRequired)
  const { startIndex: arrivalArticleStartIndex, endIndex: arrivalArticleEndIndex } = getArrivalArticleGroup(messages)
  const isWelcomeLastMessage = shouldShowWelcomeMessage && arrivalArticleEndIndex === messages.length - 1
  const welcomeMessage: ChatMessage = {
    role: 'assistant',
    content: t('welcome', { name: visibleUserName }),
    type: 'message'
  }

  const getSubjectLabel = (subject: { section: string; label: string }) =>
    tSubjects.has(subject.section) ? tSubjects(subject.section) : subject.label

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const sendTypingIndicator = useCallback(
    throttle(
      (value: string) => {
        if (canSend()) {
          sendFrame(JSON.stringify({ type: 'typing', content: value }))
        }
      },
      1500,
      { leading: true, trailing: true }
    ),
    []
  )

  const handleMessageInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInput(e.target.value)
    sendTypingIndicator(e.target.value)

    // Auto-resize textarea
    const textarea = e.target
    textarea.style.height = '0px'
    const scrollHeight = textarea.scrollHeight

    if (scrollHeight <= 44) {
      textarea.style.height = ''
    } else {
      textarea.style.height = `${scrollHeight}px`
    }
  }

  // Handle scroll to load more messages
  const handleScroll = () => {
    if (!scrollAreaRef.current) return

    const container = scrollAreaRef.current
    const scrollTop = container.scrollTop
    const scrollHeight = container.scrollHeight
    const clientHeight = container.clientHeight

    // Check if user is near bottom (within 100px threshold)
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight
    const nearBottom = distanceFromBottom < 100
    setShowScrollToBottom(!nearBottom)

    // If user scrolled to the top (within 100px threshold) and not loading
    if (scrollTop < 100 && !isLoadingHistory && hasMoreHistory) {
      const nextPage = currentPage + 1
      if (nextPage <= totalPages) {
        // Store current scroll position before loading new messages
        previousScrollHeight.current = scrollHeight

        // Request next page of messages
        requestMessageHistory(nextPage, 30)
      }
    }
  }

  // Upload files via API only (no blob download). Returns fileId per file for WS upload_file.
  const uploadFilesViaApi = async (files: File[]): Promise<{ fileId: string; file: File }[]> => {
    const results: { fileId: string; file: File }[] = []

    for (const file of files) {
      const formData = new FormData()
      if (siteId) formData.append('site_id', siteId)
      formData.append('file', file)

      const uploadRes = await fetch('/api/upload', {
        method: 'POST',
        body: formData,
        headers: { Authorization: `Bearer ${token}` }
      })

      if (!uploadRes.ok) {
        throw new Error(t('uploadError', { status: uploadRes.status }))
      }

      const uploadData = await uploadRes.json()
      const fileId = uploadData?.id ?? uploadData?.data?.id

      if (!fileId) {
        throw new Error(t('fileIdMissing'))
      }

      results.push({ fileId, file })
    }

    return results
  }

  const UPLOAD_FILE_ACK_TIMEOUT_MS = 15000

  // Wait for one upload_file.success or upload_file.error from WS
  const waitForUploadFileAck = (): Promise<void> =>
    Promise.race([
      new Promise<void>((resolve, reject) => {
        setPendingUploadFileAck({ resolve, reject })
      }),
      new Promise<void>((_, reject) =>
        setTimeout(() => {
          setPendingUploadFileAck(null)
          reject(new Error(t('fileUploadTimeout')))
        }, UPLOAD_FILE_ACK_TIMEOUT_MS)
      )
    ]).finally(() => setPendingUploadFileAck(null))

  const handleFileSelect = () => {
    fileInputRef.current?.click() // Trigger hidden input
  }

  const toggleDevMode = () => {
    setDevMode(!devMode)
  }

  const handleOpenNewChat = async () => {
    if (newTopicInFlightRef.current || isClosingChat || useSocketStore.getState().pendingNewTopicAck) return
    newTopicInFlightRef.current = true

    if (sessionId) {
      if (!canSend()) {
        setMessages(previous => [...previous, { role: 'assistant', content: t('connectionFailed'), type: 'error' }])
        newTopicInFlightRef.current = false

        return
      }

      try {
        await endCurrentSession()
      } catch (error) {
        setMessages(previous => [
          ...previous,
          {
            role: 'assistant',
            content: error instanceof Error ? error.message : t('connectionFailed'),
            type: 'error'
          }
        ])
        newTopicInFlightRef.current = false

        return
      }
    }

    setSelectedSection(null)
    setIsShowSectionSelectButtons(true)
    setMessages([])
    setOperatorName(null)
    setLiveSupportState(null)
    setIsTakenOver(false)
    setSessionId(null)
    setIsChatEnded(false)
    connectWebSocket()
    setTimeout(() => {
      setIsSecondChatShown(false)
    }, 500)
    newTopicInFlightRef.current = false
  }

  const addAttachments = (files: File[]) => {
    if (attachmentsDisabled) return
    const attachments = files.filter(isChatAttachment)
    const readers = attachments.map(
      file =>
        new Promise<{ file: File; url: string } | null>(resolve => {
          const reader = new FileReader()
          reader.onloadend = () => resolve(typeof reader.result === 'string' ? { file, url: reader.result } : null)
          reader.readAsDataURL(file)
        })
    )

    Promise.all(readers).then(results => {
      const ready = results.filter(result => result !== null)
      // Add files and previews together so rapid paste/picker events cannot pair
      // one attachment with another attachment's asynchronously loaded preview.
      setSelectedFiles(prev => [...prev, ...ready.map(result => result.file)])
      setFilePreviewUrls(prev => [...prev, ...ready.map(result => result.url)])
    })
  }

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    addAttachments(Array.from(event.target.files || []))
    event.target.value = ''
  }

  const handlePaste = (event: React.ClipboardEvent<HTMLTextAreaElement>) => {
    if (attachmentsDisabled) return
    const items = Array.from(event.clipboardData.items)
    const files = items.length
      ? items.filter(item => item.kind === 'file').map(item => item.getAsFile())
      : Array.from(event.clipboardData.files)
    const attachments = files.filter((file): file is File => !!file && isChatAttachment(file))
    if (!attachments.length) return

    event.preventDefault()
    addAttachments(attachments)
  }

  // --- Send Message ---
  const sendMessage = async () => {
    let messageToSend = input.trim()
    if (!messageToSend && selectedFiles.length === 0) return
    if (selectedFiles.length > 0 && messageToSend == '') {
      messageToSend = '[file_uploaded]'
    }

    if (socketConnectionStatus !== 'opened' || !canSend()) {
      const errorMessage = isReconnecting ? t('serverReconnecting') : t('serverInactive')

      setMessages(prev => [
        ...prev,
        {
          role: 'assistant',
          content: errorMessage,
          type: 'error'
        }
      ])
      scrollToBottom()

      return
    }

    if (isMessageResponseWaiting) return

    let uploadedFileIds: string[] = []
    const hasFiles = selectedFiles.length > 0 && filePreviewUrls.length > 0

    if (hasFiles) {
      setIsMessageResponseWaiting(true)

      try {
        const uploadResults = await uploadFilesViaApi(selectedFiles)
        uploadedFileIds = uploadResults.map(r => r.fileId)

        for (const fileId of uploadedFileIds) {
          await sendFrame(JSON.stringify({ type: 'upload_file', content: fileId }))
          await waitForUploadFileAck()
        }
      } catch (err) {
        setIsMessageResponseWaiting(false)
        const message = err instanceof Error ? err.message : t('fileUploadFailed')
        setMessages(prev => [
          ...prev,
          {
            role: 'assistant',
            content: message,
            type: 'error'
          }
        ])
        scrollToBottom()

        return
      } finally {
        // Release the composer lock once the upload flow settles. This flag is
        // otherwise only cleared by a later WS frame, which never arrives after
        // a file send, leaving the input and send button disabled until the
        // operator replies.
        setIsMessageResponseWaiting(false)
      }
    }

    const tempFilePreviewUrls =
      hasFiles && filePreviewUrls.length === selectedFiles.length
        ? selectedFiles.map((file, i) => ({
            blobUrl: filePreviewUrls[i] ?? '',
            blobType: file.type || 'application/octet-stream',
            currentFileId: uploadedFileIds[i] ?? ''
          }))
        : []

    let userMessage: ChatMessage | null = null

    if (hasFiles && tempFilePreviewUrls.length > 0) {
      const partsFileNames: string[] = []
      const fileCount = selectedFiles.length
      partsFileNames.push(`<span class="opacity-75 italic">${t('uploadedFiles', { count: fileCount })}</span>`)
      // Keep the LLM marker on the wire only — never show `[file_uploaded]` in the bubble.
      if (messageToSend && !isInternalFileUploadedMarker(messageToSend)) {
        partsFileNames.push(messageToSend)
      }

      userMessage = {
        role: 'user',
        content: partsFileNames.join('<br />'),
        type: 'file_placeholder',
        fileUrls: tempFilePreviewUrls,
        fileName: selectedFiles.map(f => f.name).join(', '),
        timestamp: Date.now()
      }
    } else if (messageToSend && !isInternalFileUploadedMarker(messageToSend)) {
      userMessage = {
        role: 'user',
        content: messageToSend,
        type: 'message',
        timestamp: Date.now()
      }
    }

    if (userMessage) {
      const clientMessageId = crypto.randomUUID()
      userMessage.client_message_id = clientMessageId
      setMessages(prev => {
        const newMessages = [...prev, userMessage]

        return newMessages
      })

      scrollToBottom()

      const outgoing = {
        content: messageToSend,
        client_message_id: clientMessageId,
        type: 'message'
      }
      sendFrame(JSON.stringify(outgoing))

      setInput('')
      if (textareaRef.current) {
        textareaRef.current.style.height = ''
      }
      setSelectedFiles([])
      setFilePreviewUrls([])
      scrollToBottom()
    }
  }

  const handleFocusTextarea = async () => {
    await new Promise(resolve => setTimeout(resolve, 200))
    textareaRef.current?.focus()
  }

  useEffect(() => {
    if (!isMessageResponseWaiting) {
      handleFocusTextarea()
    }
  }, [isMessageResponseWaiting])

  useEffect(() => {
    return () => {
      sendTypingIndicator.cancel()
    }
  }, [sendTypingIndicator])

  const showComposer = isChatEnded || isAuthSuccess

  return (
    <div className='relative flex size-full flex-col overflow-hidden'>
      {isPreparing && (
        <div className='bg-background/50 absolute inset-x-0 top-[68px] bottom-0 z-99 flex items-center justify-center p-2 text-center'>
          <div className='text-muted-foreground flex flex-col items-center justify-center gap-2 text-xs font-medium'>
            <Loader2 className='size-4 animate-spin' />
            <p>{t('preparing')}</p>
          </div>
        </div>
      )}

      {isReconnecting && token && (
        <div className='absolute top-[68px] right-0 left-0 z-50 mb-2 flex h-12 items-center justify-center gap-2 bg-yellow-100 text-center text-sm text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'>
          <WifiOff size={16} /> {t('reconnecting', { attempt: retryAttempt })}
        </div>
      )}

      {!wsRef.current &&
        socketConnectionStatus === 'idle' &&
        !isReconnecting &&
        messages.length > 0 && ( // Not an initial error
          <div className='mb-2 flex items-center justify-center gap-2 bg-red-100 p-2 text-center text-sm text-red-800 dark:bg-red-900 dark:text-red-200'>
            <WifiOff size={16} /> {t('connectionClosed')}
          </div>
        )}

      {socketConnectionStatus === 'error' && !isReconnecting && (
        <div className='mb-2 flex items-center justify-center gap-2 bg-red-100 p-2 text-center text-sm text-red-800 dark:bg-red-900 dark:text-red-200'>
          <WifiOff size={16} /> {t('connectionError')}
          <Button size='sm' variant='outline' onClick={() => window.location.reload()} className='ml-2 h-6 text-xs'>
            {t('retry')}
          </Button>
        </div>
      )}

      <div className='z-40 shrink-0'>
        <ChatModalHeader onOpenNewChat={handleOpenNewChat} />
      </div>

      {isAuthSuccess && activeTab === 'tickets' ? (
        <TicketsPanel />
      ) : (
        <>
          <div className='relative flex min-h-0 flex-1 flex-col overflow-hidden'>
            {/* Post-session review: overlay fills the content area below the header,
            so the minimize/header controls stay reachable. Self-managed fade. */}
            {pendingReview && <LiveSupportReviewCard key={pendingReview.session_id} />}

            <FloatScrollToBottomButton />

            <div
              className='size-full overflow-x-hidden overflow-y-auto overscroll-contain'
              ref={scrollAreaRef}
              onScroll={handleScroll}
            >
              <div className='flex flex-col gap-4 p-4'>
                {/* Loading indicator for message history */}
                {isLoadingHistory && (
                  <div className='flex justify-center py-4'>
                    <div className='text-muted-foreground flex items-center gap-2 text-xs font-medium'>
                      <Loader2 className='size-4 animate-spin' />
                      <p>{t('loadingHistory')}</p>
                    </div>
                  </div>
                )}

                {shouldShowWelcomeMessage && arrivalArticleStartIndex === -1 && (
                  <MessageItem
                    key='welcome-message'
                    message={welcomeMessage}
                    isLastMessage={isWelcomeLastMessage}
                    ref={isWelcomeLastMessage ? lastMessageRef : null}
                  />
                )}

                {messages?.map((msg, i) => (
                  <Fragment key={msg.timestamp ? `${msg.timestamp}-${i}` : `${i}`}>
                    {msg.timestamp &&
                      (i === 0 ||
                        !messages[i - 1].timestamp ||
                        new Date(messages[i - 1].timestamp!).toDateString() !==
                          new Date(msg.timestamp).toDateString()) && (
                        <div className='text-muted-foreground my-2 flex items-center gap-4 text-[11px] capitalize'>
                          <span className='bg-border h-px flex-1' />
                          <span>
                            {new Date(msg.timestamp).toDateString() === new Date().toDateString()
                              ? new Intl.RelativeTimeFormat(locale, { numeric: 'auto' }).format(0, 'day')
                              : new Intl.DateTimeFormat(locale, {
                                  day: 'numeric',
                                  month: 'short',
                                  year: 'numeric'
                                }).format(msg.timestamp)}
                          </span>
                          <span className='bg-border h-px flex-1' />
                        </div>
                      )}
                    {msg.type !== 'separator' && (
                      <MessageItem
                        message={msg}
                        isLastMessage={i === messages.length - 1 && !isWelcomeLastMessage}
                        ref={i === messages.length - 1 && !isWelcomeLastMessage ? lastMessageRef : null}
                      />
                    )}

                    {shouldShowWelcomeMessage && i === arrivalArticleEndIndex && (
                      <MessageItem
                        message={welcomeMessage}
                        isLastMessage={isWelcomeLastMessage}
                        ref={isWelcomeLastMessage ? lastMessageRef : null}
                      />
                    )}
                  </Fragment>
                ))}
              </div>
            </div>
          </div>

          {/* File Preview Area */}
          {filePreviewUrls.length > 0 && (
            <div className='relative grid grid-cols-4 items-center gap-2 border-t px-2 py-1 sm:grid-cols-6'>
              {filePreviewUrls.map((url, i) => (
                <div key={i} className='relative overflow-hidden rounded-xl border'>
                  {selectedFiles[i]?.type.startsWith('video/') ? (
                    <video
                      src={url}
                      controls
                      playsInline
                      preload='metadata'
                      className='aspect-square size-full object-contain'
                    />
                  ) : url.includes('application/pdf') ? (
                    <div className='flex aspect-square size-full flex-col items-center justify-center gap-1 bg-gray-300 text-gray-500 dark:bg-gray-600 dark:text-gray-400'>
                      <FileText size={24} />
                      <b className='text-xs'>PDF</b>
                    </div>
                  ) : (
                    <img
                      src={url}
                      alt={t('previewAlt', { index: i })}
                      className='aspect-square size-full object-cover'
                    />
                  )}
                  <Button
                    disabled={isMessageResponseWaiting}
                    variant='destructive'
                    size='icon'
                    onClick={() => {
                      const newFiles = [...selectedFiles]
                      const newUrls = [...filePreviewUrls]
                      newFiles.splice(i, 1)
                      newUrls.splice(i, 1)
                      setSelectedFiles(newFiles)
                      setFilePreviewUrls(newUrls)
                    }}
                    className='absolute top-1 right-1 size-6 opacity-75 hover:opacity-100'
                  >
                    <XCircle size={16} />
                  </Button>
                </div>
              ))}
            </div>
          )}

          {!isAuthSuccess && !isPreparing && !isReconnecting && !isChatEnded && (
            <div className='bg-muted/30 border-t p-2'>
              <div className='mb-1 flex items-center gap-1 text-xs font-medium text-red-600'>
                <ShieldUser className='size-4' />
                {t('authErrorTitle')}
              </div>
              <div className='text-muted-foreground mb-2 text-xs break-words'>{t('authErrorDescription')}</div>
              <div className='flex items-center gap-2'>
                <Button
                  onClick={() => {
                    resetStore()
                  }}
                  className='w-full'
                  disabled={isReconnecting}
                >
                  {isReconnecting ? <Loader2 className='animate-spin' /> : <RefreshCcw />}
                  <span>{t('loginAgain')}</span>
                </Button>
              </div>
            </div>
          )}

          {/* Topic Selection - shown on fresh chat or when explicitly reopened */}
          {isAuthSuccess &&
            sessionId &&
            !isChatEnded &&
            !selectedSection &&
            subjects.length > 0 &&
            (isShowSectionSelectButtons || messages.length === 0) && (
              <div className='border-border/50 shrink-0 border-t p-3'>
                <div className='bg-background rounded-2xl border p-3'>
                  <p className='mb-2 text-xs font-bold'>{t('selectTopic')}</p>
                  <div className='grid grid-cols-3 gap-2'>
                    {subjects.map(subject => {
                      const label = getSubjectLabel(subject)
                      const TopicIcon =
                        subject.section === 'investment_controls'
                          ? CreditCard
                          : subject.section === 'bonuses'
                            ? Gift
                            : Ellipsis

                      return (
                        <button
                          key={subject.section}
                          onClick={() => handleSelectSubject(subject.section, label)}
                          disabled={socketConnectionStatus !== 'opened' || isReconnecting}
                          className='border-primary/25 bg-primary/5 hover:bg-primary/10 flex min-w-0 cursor-pointer items-center gap-2 rounded-xl border px-2 py-2.5 text-start transition-colors disabled:cursor-not-allowed disabled:opacity-50'
                        >
                          <span className='bg-primary/15 text-primary flex size-6 shrink-0 items-center justify-center rounded-lg'>
                            <TopicIcon className='size-3.5' />
                          </span>
                          <span className='min-w-0 text-[11px] leading-tight font-semibold break-words'>{label}</span>
                        </button>
                      )
                    })}
                  </div>
                </div>
              </div>
            )}

          {/* Composer stays visible beside topic selection, enabled once a topic is chosen. */}
          {showComposer && (
            <div className='border-border/50 shrink-0 border-t p-3.5'>
              <ContextMenu>
                <ContextMenuTrigger>
                  {isChatEnded ? (
                    <div className='flex flex-col gap-2'>
                      <p className='text-muted-foreground text-center text-sm'>{t('chatEnded')}</p>
                      <Button onClick={handleOpenNewChat} className='h-10 w-full'>
                        {t('openNewChat')}
                      </Button>
                    </div>
                  ) : (
                    <div className='flex h-full items-center gap-2.5'>
                      {apiUrl && (
                        <button
                          className='bg-secondary text-muted-foreground hover:text-foreground hover:bg-muted flex size-10 shrink-0 cursor-pointer items-center justify-center rounded-full transition-all active:scale-95 disabled:cursor-not-allowed disabled:opacity-50'
                          onClick={handleFileSelect}
                          disabled={attachmentsDisabled}
                          title={t('uploadDocument')}
                        >
                          <Paperclip className='size-5' />
                          <input
                            type='file'
                            ref={fileInputRef}
                            onChange={handleFileChange}
                            accept={CHAT_ATTACHMENT_ACCEPT}
                            className='hidden'
                            multiple={true}
                          />
                        </button>
                      )}

                      <div className='relative min-w-0 flex-1'>
                        <Textarea
                          rows={1}
                          ref={textareaRef}
                          autoFocus
                          value={input}
                          onChange={handleMessageInputChange}
                          onPaste={handlePaste}
                          onKeyDown={e => {
                            if (!sendMessageDisabled && e.key === 'Enter' && !e.shiftKey) {
                              e.preventDefault()
                              sendMessage()
                            }
                          }}
                          disabled={
                            requiresTopic ||
                            isMessageResponseWaiting ||
                            isGuestInfoRequired ||
                            socketConnectionStatus === 'loading' ||
                            isReconnecting
                          }
                          placeholder={
                            isGuestInfoRequired
                              ? t('guestInfoRequired')
                              : socketConnectionStatus === 'loading'
                                ? t('waitingConnection')
                                : isReconnecting
                                  ? t('reconnectingInput')
                                  : isMessageResponseWaiting
                                    ? t('waitingResponse')
                                    : t('writeMessage')
                          }
                          className='bg-card focus-visible:border-primary focus-visible:ring-primary placeholder:text-muted-foreground field-sizing-fixed h-11 max-h-50 min-h-11 resize-none overflow-y-auto rounded-[22px] border py-2.5 ps-4 pe-10 text-[13px] leading-5 shadow-none placeholder-shown:overflow-hidden placeholder-shown:text-ellipsis placeholder-shown:whitespace-nowrap focus-visible:ring-1 md:text-sm'
                        />

                        <Popover open={emojiOpen} onOpenChange={setEmojiOpen}>
                          <PopoverTrigger asChild>
                            <button
                              type='button'
                              aria-label='Emoji'
                              disabled={
                                requiresTopic ||
                                isMessageResponseWaiting ||
                                isGuestInfoRequired ||
                                isReconnecting ||
                                socketConnectionStatus !== 'opened'
                              }
                              className='hover:text-primary text-muted-foreground absolute end-3 top-2.5 disabled:opacity-40'
                            >
                              <Smile className='size-5' />
                            </button>
                          </PopoverTrigger>
                          <PopoverContent className='w-auto rounded-2xl p-2' side='top' align='end'>
                            <div className='grid grid-cols-4 gap-1'>
                              {['😀', '😊', '👍', '🙏', '❤️', '🎉', '👋', '🙂'].map(emoji => (
                                <button
                                  key={emoji}
                                  type='button'
                                  className='hover:bg-muted size-9 rounded-lg text-xl'
                                  onClick={() => {
                                    setInput(value => value + emoji)
                                    setEmojiOpen(false)
                                    textareaRef.current?.focus()
                                  }}
                                >
                                  {emoji}
                                </button>
                              ))}
                            </div>
                          </PopoverContent>
                        </Popover>
                      </div>
                      <button
                        aria-label={tForm('submit')}
                        onClick={sendMessage}
                        disabled={sendMessageDisabled}
                        className='bg-primary text-primary-foreground flex size-11 shrink-0 cursor-pointer items-center justify-center rounded-full shadow-md transition-all hover:brightness-110 active:scale-95 disabled:cursor-not-allowed disabled:opacity-30'
                      >
                        {selectedFiles.length > 0 && isMessageResponseWaiting && socketConnectionStatus === 'opened' ? (
                          <Loader2 className='size-4 animate-spin' />
                        ) : (
                          <Send className='size-5' />
                        )}
                      </button>
                    </div>
                  )}
                </ContextMenuTrigger>
                <ContextMenuContent>
                  <ContextMenuItem onClick={toggleDevMode}>
                    <Settings className='size-4' />
                    {devMode ? t('disableDevMode') : t('enableDevMode')}
                  </ContextMenuItem>
                </ContextMenuContent>
              </ContextMenu>
            </div>
          )}
        </>
      )}

      {/* Development Mode Section */}
      {devMode && (
        <div className='relative z-40 px-2 pb-2'>
          <div className='rounded-xl border border-dashed border-red-500 p-2 text-xs'>
            <p className='!font-mono text-red-500'>{t('devMode')}</p>
            <div className='mt-1 flex flex-wrap gap-1 py-1'>
              <Button
                size='sm'
                variant='secondary'
                onClick={() => {
                  wsRef.current?.close()
                }}
              >
                {t('resetWsStatus')}
              </Button>
              <Button
                size='sm'
                variant='secondary'
                onClick={() => {
                  resetStore()
                }}
              >
                {t('resetStore')}
              </Button>
            </div>
            <div className='mt-2 border-t border-dashed border-red-500 pt-2 !font-mono'>
              <p>
                version: <b className='text-blue-500'>{buildVersion}</b>
              </p>
              <p>
                siteId: <b className='text-blue-500'>{siteId}</b>
              </p>
              <p>
                token: <b className='text-blue-500'>{token} </b>
              </p>
              <p>
                isGuestIdActive: <b className='text-blue-500'>{isGuestIdActive ? 'true' : 'false'}</b>
              </p>
              <p>
                guestId: <b className='text-blue-500'>{guestId}</b>
              </p>
              <p>
                sessionId: <b className='text-blue-500'>{sessionId}</b>
              </p>
              <p>
                wsRef.current.readyState: <b className='text-blue-500'>{wsRef.current?.readyState}</b>
              </p>
              <p>
                socketConnectionStatus: <b className='text-blue-500'>{socketConnectionStatus}</b>
              </p>
              <p>
                isAuthSuccess: <b className='text-blue-500'>{isAuthSuccess ? 'true' : 'false'}</b>
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
